/**
 * The synthetic candidate set behind the self-serve trial.
 *
 * SIMULATED. These twelve people do not exist. The set is lifted verbatim from
 * the approved prototype so the trial behaves identically, and it lives in its
 * own module precisely so nothing here can be mistaken for a read against the
 * real candidate store.
 *
 * Note what these records do NOT have: a name, an email, a phone number, a
 * resume, or an employer. That is deliberate and it drives the copy. The
 * trial's mask/unmask boundary is a real mechanic, but no identity sits behind
 * it here -- so the UI describes a boundary and never claims to be concealing
 * a particular person's name. See MASK_HAS_NOTHING_BEHIND_IT.
 *
 * When real sourcing is wired in, this module is what gets replaced. Nothing
 * else in the flow should need to change.
 */

/**
 * True while the candidate set is synthetic. The trial reads this to choose
 * wording that is true of simulated data over wording implying a withheld
 * identity. Flipping it is a deliberate act, paired with replacing the data.
 */
export const MASK_HAS_NOTHING_BEHIND_IT = true;

export type Education = 'high school' | 'certification' | 'bachelor';

export interface SyntheticCandidate {
  /** A letter, not a person. There is no name field, by design. */
  id: string;
  domain: string;
  skills: string[];
  years: number;
  education: Education;
  bio: string;
  highlights: string[];
}

/**
 * Skill vocabulary the JD parser recognises, verbatim from the prototype. The
 * parser substring-matches against these terms, which is why the trial says it
 * recognised keywords rather than claiming to have understood a job posting.
 */
export const SKILL_DICT: readonly string[] = [
  'python',
  'sql',
  'aws',
  'javascript',
  'react',
  'typescript',
  'css',
  'api design',
  'distributed systems',
  'docker',
  'kubernetes',
  'testing',
  'sales',
  'salesforce',
  'negotiation',
  'prospecting',
  'communication',
  'crm',
  'marketing',
  'seo',
  'content',
  'analytics',
  'campaign management',
  'electrical',
  'safety certification',
  'osha',
  'wiring',
  'blueprint reading',
  'troubleshooting',
  'hvac',
  'maintenance',
  'construction',
  'forklift',
  'logistics',
  'teamwork',
  'customer service',
  'conflict resolution',
  'scheduling',
  'retail',
  'inventory management',
  'excel',
  'office administration',
  'project management',
  'budgeting',
  'reporting',
  'leadership',
];

export const CANDIDATES: readonly SyntheticCandidate[] = [
  {
    id: 'A',
    domain: 'backend engineering',
    skills: [
      'python',
      'sql',
      'aws',
      'javascript',
      'api design',
      'distributed systems',
      'docker',
      'kubernetes',
    ],
    years: 6,
    education: 'bachelor',
    bio: "Backend-focused engineer who's spent most of their career owning services end to end, from design through production.",
    highlights: [
      'Led a multi-region service migration with zero downtime',
      'Built an API layer that now serves several million requests a day',
      "Runs point on the team's on-call rotation and incident response",
    ],
  },
  {
    id: 'B',
    domain: 'frontend engineering',
    skills: [
      'javascript',
      'react',
      'css',
      'typescript',
      'api design',
      'testing',
    ],
    years: 4,
    education: 'bachelor',
    bio: 'Frontend engineer focused on product surfaces used directly by paying customers.',
    highlights: [
      'Rebuilt a core product surface now used by 50,000+ daily users',
      'Cut page load times by more than half through a rendering overhaul',
      'Partners closely with design to ship polished, well-tested UI',
    ],
  },
  {
    id: 'C',
    domain: 'sales',
    skills: [
      'sales',
      'salesforce',
      'negotiation',
      'prospecting',
      'communication',
      'crm',
    ],
    years: 5,
    education: 'bachelor',
    bio: 'Full-cycle sales rep comfortable owning a pipeline from first call to signed contract.',
    highlights: [
      'Exceeded quota in 7 of the last 8 quarters',
      'Built a structured outbound prospecting motion from scratch',
      "Keeps detailed CRM notes that shorten the next rep's ramp-up",
    ],
  },
  {
    id: 'D',
    domain: 'marketing',
    skills: [
      'marketing',
      'seo',
      'content',
      'analytics',
      'campaign management',
      'communication',
    ],
    years: 3,
    education: 'bachelor',
    bio: 'Growth-minded marketer who pairs content work with hard analytics.',
    highlights: [
      'Grew organic traffic 3x in 18 months',
      'Ran an always-on content calendar across three channels',
      'Reports weekly on campaign performance against pipeline targets',
    ],
  },
  {
    id: 'E',
    domain: 'electrical trades',
    skills: [
      'electrical',
      'safety certification',
      'osha',
      'wiring',
      'blueprint reading',
      'troubleshooting',
    ],
    years: 8,
    education: 'certification',
    bio: 'Certified journeyman electrician with deep commercial site experience.',
    highlights: [
      'Has worked commercial and industrial job sites for over eight years',
      'Comfortable reading complex blueprints and electrical schematics',
      'Trains newer crew members on site safety protocol',
    ],
  },
  {
    id: 'F',
    domain: 'HVAC technician',
    skills: [
      'hvac',
      'safety certification',
      'osha',
      'troubleshooting',
      'maintenance',
      'blueprint reading',
    ],
    years: 5,
    education: 'certification',
    bio: 'HVAC technician specializing in large-scale commercial cooling systems.',
    highlights: [
      'Services and maintains commercial-scale cooling systems daily',
      'Typically resolves system issues without needing a second visit',
      'Holds the safety certifications required for active job sites',
    ],
  },
  {
    id: 'G',
    domain: 'construction / general labor',
    skills: [
      'construction',
      'safety certification',
      'osha',
      'forklift',
      'logistics',
      'teamwork',
    ],
    years: 4,
    education: 'high school',
    bio: 'Construction crew lead with ground-up commercial build experience.',
    highlights: [
      'Led a crew across multiple ground-up commercial builds',
      'Coordinates daily with other trades to keep projects on schedule',
      'Clean safety record across four years on active job sites',
    ],
  },
  {
    id: 'H',
    domain: 'customer service',
    skills: [
      'customer service',
      'communication',
      'crm',
      'conflict resolution',
      'scheduling',
    ],
    years: 3,
    education: 'high school',
    bio: 'Customer service rep known for turning frustrated callers into repeat customers.',
    highlights: [
      'Consistently rated top-tier in customer satisfaction surveys',
      'Handles escalations calmly and knows when to loop in a supervisor',
      'Works phone, chat, and email queues within the same shift',
    ],
  },
  {
    id: 'I',
    domain: 'retail operations',
    skills: [
      'retail',
      'inventory management',
      'scheduling',
      'customer service',
      'excel',
    ],
    years: 4,
    education: 'high school',
    bio: 'Retail operator focused on keeping inventory and service levels tight.',
    highlights: [
      'Kept inventory accuracy above 98% across two store locations',
      'Trains new hires on POS systems and store procedures',
      'Balances front-of-house service with backend inventory work',
    ],
  },
  {
    id: 'J',
    domain: 'office administration',
    skills: [
      'excel',
      'scheduling',
      'communication',
      'project management',
      'budgeting',
    ],
    years: 5,
    education: 'bachelor',
    bio: "Office administrator who keeps a growing team's day-to-day running smoothly.",
    highlights: [
      'Coordinated cross-team operations for a 40-person office',
      'Owns budgeting and vendor management for office operations',
      'Built the scheduling system the team still relies on today',
    ],
  },
  {
    id: 'K',
    domain: 'logistics / warehouse',
    skills: [
      'logistics',
      'forklift',
      'inventory management',
      'safety certification',
      'osha',
      'teamwork',
    ],
    years: 6,
    education: 'high school',
    bio: 'Warehouse operations lead used to running high-volume distribution sites.',
    highlights: [
      'Oversaw daily throughput for a high-volume distribution site',
      'Reduced picking errors through a revised floor layout',
      'Certified on forklift operation and daily safety checks',
    ],
  },
  {
    id: 'L',
    domain: 'data / analytics',
    skills: [
      'sql',
      'python',
      'excel',
      'analytics',
      'communication',
      'reporting',
    ],
    years: 4,
    education: 'bachelor',
    bio: 'Analyst who turns messy operational data into decisions people actually use.',
    highlights: [
      'Built reporting that cut manual analysis time by 70%',
      'Partners directly with ops leadership on recurring reporting needs',
      'Comfortable working across SQL, spreadsheets, and BI tools',
    ],
  },
];
